import React, { useState } from 'react';
import CommonSection from '../components/Ui/CommonSection';
import Helmet from '../components/Helmet/Helmet';
import { Container, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { wishlistActions } from '../redux/slices/wishlistSlice';
import { cartActions } from '../redux/slices/cartSlice';
import { toast } from 'react-toastify';
import useAuth from '../custom-hooks/useAuth';
import ConfirmModal from '../components/Ui/ConfirmModal';
import { getProductImg, handleImgError } from '../utils/imageHelper';
import '../styles/wishlist.css';

const Wishlist = () => {
  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  const handleRemove = (id) => {
    dispatch(wishlistActions.removeFromWishlist(id));
    toast.info('Item removed from wishlist');
  };

  const handleMoveToCart = (item) => {
    if (!currentUser) {
      toast.info('Please sign in to add items to your cart & sync your profile');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    dispatch(
      cartActions.addItem({
        id: item.id,
        productName: item.productName,
        price: item.price,
        imgUrl: item.imgUrl,
      })
    );
    dispatch(wishlistActions.removeFromWishlist(item.id));
    toast.success('Moved piece to shopping bag!');
  };

  return (
    <Helmet title="My Wishlist | L'HABILLEUR">
      <CommonSection title="Saved Pieces" />

      <section className="wishlist__section py-5">
        <Container>
          {wishlistItems.length === 0 ? (
            <div className="wishlist__empty text-center py-5">
              <div className="wishlist__empty-icon mb-3">
                <i className="ri-heart-3-line"></i>
              </div>
              <h3 className="fw-bold mb-2">Your Wishlist is Empty</h3>
              <p className="text-muted mb-4">
                Explore our collections and tap the heart icon to save your favorite pieces for later.
              </p>
              <Link to="/shop" className="buy__btn">
                <i className="ri-store-2-line"></i> Explore Atelier
              </Link>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                <h5 className="fw-bold m-0" style={{ color: 'var(--primary-color)' }}>
                  Saved Pieces ({wishlistItems.length})
                </h5>
                <button
                  type="button"
                  onClick={() => setIsClearModalOpen(true)}
                  className="btn btn-sm btn-outline-danger"
                >
                  <i className="ri-delete-bin-line me-1"></i> Clear All
                </button>
              </div>

              <Row className="g-4">
                {wishlistItems.map((item) => (
                  <Col key={item.id} lg="3" md="4" sm="6">
                    <div className="wishlist__card">
                      <button
                        type="button"
                        onClick={() => handleRemove(item.id)}
                        className="wishlist__card-remove"
                        title="Remove from wishlist"
                        aria-label="Remove item"
                      >
                        <i className="ri-close-line"></i>
                      </button>

                      <Link to={`/shop/${item.id}`} className="wishlist__card-img-link">
                        <img
                          src={getProductImg(item)}
                          onError={(e) => handleImgError(e, item)}
                          alt={item.productName}
                          className="wishlist__card-img"
                        />
                      </Link>

                      <div className="wishlist__card-body">
                        <span className="wishlist__card-cat">{item.category}</span>
                        <h6 className="wishlist__card-title">
                          <Link to={`/shop/${item.id}`}>{item.productName}</Link>
                        </h6>
                        <div className="wishlist__card-price">${item.price}</div>

                        <button
                          type="button"
                          onClick={() => handleMoveToCart(item)}
                          className="wishlist__move-btn w-100 mt-2"
                        >
                          <i className="ri-shopping-bag-line me-1"></i> Move to Bag
                        </button>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Container>
      </section>

      {/* Clear Wishlist Confirmation Modal */}
      <ConfirmModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        onConfirm={() => {
          dispatch(wishlistActions.clearWishlist());
          setIsClearModalOpen(false);
          toast.info('All saved pieces removed from wishlist');
        }}
        title="Clear All Saved Pieces?"
        badge="Wishlist Action"
        message="Are you sure you want to remove all saved items from your wishlist? This action will immediately clear your saved list."
        confirmText="Yes, Clear All"
        cancelText="Keep Pieces"
        confirmVariant="danger"
        icon="ri-delete-bin-line"
      />
    </Helmet>
  );
};

export default Wishlist;
